// MusteriListesi.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  getFirestore,
  collection,
  onSnapshot,
} from 'firebase/firestore';
import {
  Container,
  Typography,
  Button,
  Snackbar,
  Alert,
  TextField,
  Box,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { styled } from '@mui/material/styles';
import { auth } from './firebaseConfig';
import { transformFirestoreData } from '../utils/databaseOperations';
import { COLLECTION_PATHS } from '../utils/constants';
import { turkishMultiSearch, toTurkishUpperCase } from '../utils/searchUtils';

import CustomerTable from './CustomerTable';
import EditCustomerModal from './EditCustomerModal';
import EditSantiyeModal from './EditSantiyeModal';
import AddCustomerModal from './AddCustomerModal';
import DeleteCustomerModal from './DeleteCustomerModal';
import DeleteSantiyeModal from './DeleteSantiyeModal';
import TransferCustomerModal from './TransferCustomerModal';
import TransferHistoryModal from './TransferHistoryModal';
import ExcelImportModal from './ExcelImportModal';

// Arama kutusu için stil
const SearchBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(2)
}));

const MusteriListesi = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [approvedCustomers, setApprovedCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditSantiyeModalOpen, setIsEditSantiyeModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteSantiyeModalOpen, setIsDeleteSantiyeModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isExcelImportModalOpen, setIsExcelImportModalOpen] = useState(false);
  const [isTransferHistoryModalOpen, setIsTransferHistoryModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [customerShantiyeler, setCustomerShantiyeler] = useState([]);

  const [selectedSantiye, setSelectedSantiye] = useState(null);
  const [selectedCustomerForTransfer, setSelectedCustomerForTransfer] = useState(null);



  useEffect(() => {
    const db = getFirestore();
    const currentUser = auth.currentUser;

    if (!currentUser?.email) {
      console.error('Kullanıcı oturumu bulunamadı');
      setError('Oturum bilgisi alınamadı');
      setLoading(false);
      return;
    }

    const customerListRef = collection(db, COLLECTION_PATHS.USER_CUSTOMERS(currentUser.email));
    
    const unsubscribe = onSnapshot(
      customerListRef,
      {
        includeMetadataChanges: true
      },
      (snapshot) => {
        try {
          const customerData = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              'Müşteri Adı': data['Müşteri Adı'] || '',
              'Onay': data['Onay'] || '',
              cariCode: data['cariCode'] || '',
              Şantiye: data['Şantiye'] || false,
              parentId: data.parentId || null,
            };
          });

          const approvedData = customerData.filter(customer => {
            const onayDurumu = customer['Onay'];
            return onayDurumu === 'Onaylandı';
          });

          setApprovedCustomers(approvedData);
          
          const santiyeler = approvedData.filter(customer => customer.Şantiye);
          setCustomerShantiyeler(santiyeler);
          
          setLoading(false);
        } catch (error) {
          console.error('Veri işlenirken hata:', error);
          setError('Veriler işlenirken bir hata oluştu');
          setLoading(false);
        }
      }
    );

    return () => unsubscribe();
  }, []);

  const openEditModal = (customer) => {
    setSelectedCustomer(customer);
    setIsEditModalOpen(true);
  };

  const openEditSantiyeModal = (santiye) => {
    setSelectedSantiye(santiye);
    setIsEditSantiyeModalOpen(true);
  };

  const openDeleteModal = (customer) => {
    setSelectedCustomer(customer);
    setIsDeleteModalOpen(true);
  };

  const openDeleteSantiyeModal = (santiye) => {
    setSelectedSantiye(santiye);
    setIsDeleteSantiyeModalOpen(true);
  };

  const handleTransferSantiye = (santiye) => {
    console.log('Transfer edilecek şantiye:', santiye);
    setSelectedCustomerForTransfer(santiye);
    setIsTransferModalOpen(true);
  };

  const handleTransferCustomer = (customer) => {
    console.log('Transfer edilecek müşteri:', customer);
    setSelectedCustomerForTransfer(customer);
    setIsTransferModalOpen(true);
  };

  const handleCloseAlert = () => {
    setAlertOpen(false);
    setError(null);
    setSuccessMessage('');
  };

  const getCustomersWithŞantiye = () => {
    const mainCustomers = approvedCustomers.filter(customer => customer.parentId === null);
    return mainCustomers.map(customer => ({
      ...customer,
      şantiyeler: approvedCustomers.filter(şantiye => şantiye.parentId === customer.id)
    }));
  };

  const handleTransferModalClose = () => {
    setIsTransferModalOpen(false);
    setSelectedCustomerForTransfer(null);
  };

  const getFilteredCustomers = () => {
    const customers = getCustomersWithŞantiye();
    if (!searchTerm) return customers;

    return customers.filter(customer => {
      // Türkçe karakterlere uygun arama - müşteri
      const matchesSearch = turkishMultiSearch(searchTerm, [
        customer['Müşteri Adı'] || '',
        customer.cariCode || ''
      ]);

      // Şantiyelerde de ara - Türkçe karakterlere uygun
      const hasMatchingŞantiye = customer.şantiyeler?.some(şantiye => {
        return turkishMultiSearch(searchTerm, [
          şantiye['Şantiye Adı'] || '',
          şantiye['Şantiye Cari Kodu'] || ''
        ]);
      });

      return matchesSearch || hasMatchingŞantiye;
    });
  };

  if (loading) {
    return (
      <Container>
        <Typography>Yükleniyor...</Typography>
      </Container>
    );
  }

  if (error && !selectedCustomer) {
    return (
      <Container>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Cari Tanıtım
      </Typography>

      <SearchBox>
        <TextField
          fullWidth
          placeholder="Müşteri adı veya cari kod ile arama yapın..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(toTurkishUpperCase(e.target.value))}
          InputProps={{
            startAdornment: <SearchIcon color="action" />,
          }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => setIsAddModalOpen(true)}
        >
          Yeni Müşteri Ekle
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => setIsExcelImportModalOpen(true)}
          sx={{ ml: 1 }}
        >
          📊 Excel'den İçe Aktar
        </Button>
        <Button
          variant="outlined"
          color="warning"
          onClick={() => setIsTransferHistoryModalOpen(true)}
          sx={{ ml: 1 }}
        >
          ⏪ Transfer Geçmişi
        </Button>
      </SearchBox>

      {loading ? (
        <Typography>Yükleniyor...</Typography>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <CustomerTable
          customers={getFilteredCustomers()}
          onEdit={openEditModal}
          onEditSantiye={openEditSantiyeModal}
          onDelete={openDeleteModal}
          onDeleteSantiye={openDeleteSantiyeModal}
          onTransferSantiye={handleTransferSantiye}
          onTransferCustomer={handleTransferCustomer}
        />
      )}

      <div style={{ display: 'none' }}>
        <p>Onaylı Müşteri Sayısı: {approvedCustomers.length}</p>
        <pre>{JSON.stringify(approvedCustomers, null, 2)}</pre>
      </div>

      <EditCustomerModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        selectedCustomer={selectedCustomer}
        setSelectedCustomer={setSelectedCustomer}
        approvedCustomers={approvedCustomers}
        setAlertOpen={setAlertOpen}
        setError={setError}
        setSuccessMessage={setSuccessMessage}
      />

      <EditSantiyeModal
        isOpen={isEditSantiyeModalOpen}
        onClose={() => setIsEditSantiyeModalOpen(false)}
        selectedSantiye={selectedSantiye}
        setSelectedSantiye={setSelectedSantiye}
        approvedCustomers={approvedCustomers}
        setAlertOpen={setAlertOpen}
        setError={setError}
        setSuccessMessage={setSuccessMessage}
      />

      <AddCustomerModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        setAlertOpen={setAlertOpen}
        setError={setError}
        setSuccessMessage={setSuccessMessage}
      />

      <DeleteCustomerModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        selectedCustomer={selectedCustomer}
        setAlertOpen={setAlertOpen}
        setError={setError}
        setSuccessMessage={setSuccessMessage}
        setSelectedCustomer={setSelectedCustomer}
        onTransfer={(customer) => {
          setIsDeleteModalOpen(false);
          setSelectedCustomerForTransfer(customer);
          setIsTransferModalOpen(true);
        }}
      />

      <DeleteSantiyeModal
        isOpen={isDeleteSantiyeModalOpen}
        onClose={() => setIsDeleteSantiyeModalOpen(false)}
        selectedSantiye={selectedSantiye}
        setCustomerShantiyeler={setCustomerShantiyeler}
        customerShantiyeler={customerShantiyeler}
        setAlertOpen={setAlertOpen}
        setError={setError}
        setSuccessMessage={setSuccessMessage}
        onTransfer={(santiye) => {
          setIsDeleteSantiyeModalOpen(false);
          setSelectedCustomerForTransfer(santiye);
          setIsTransferModalOpen(true);
        }}
      />

      <TransferCustomerModal
        isOpen={isTransferModalOpen}
        onClose={handleTransferModalClose}
        selectedCustomer={selectedCustomerForTransfer}
        setAlertOpen={setAlertOpen}
        setError={setError}
        setSuccessMessage={setSuccessMessage}
        isSantiyeTransfer={false}
      />

      <ExcelImportModal
        isOpen={isExcelImportModalOpen}
        onClose={() => setIsExcelImportModalOpen(false)}
        setAlertOpen={setAlertOpen}
        setError={setError}
        setSuccessMessage={setSuccessMessage}
      />

      <TransferHistoryModal
        open={isTransferHistoryModalOpen}
        onClose={() => setIsTransferHistoryModalOpen(false)}
        userEmail={auth.currentUser?.email}
      />

      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={error ? 'error' : 'success'}
          sx={{ width: '100%' }}
        >
          {error || successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MusteriListesi;
